import { skipToken } from '@apollo/client/react'
import { useLocalStorage } from '@mantine/hooks'
import { FormControl, Radio, RadioGroup } from '@mui/material'
import { pick } from 'lodash-es'
import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'react-toastify'
import { tv } from 'tailwind-variants'
import { match } from 'ts-pattern'
import EmojioneV1LightningMood from '~icons/emojione-v1/lightning-mood'
import MingcuteTransferLine from '~icons/mingcute/transfer-line'
import { openSignModal } from 'src/components/Account/utils'
import ModelSelectDialog, {
  ModelCornerBadge,
  ModelControlLabel,
} from 'src/components/GenerationModel/ModelSelectDialog'
import { UITooltipHelpIcon } from 'src/components/HelpIcon'
import { openMembershipFeatureDialog } from 'src/components/Membership/MembershipFeatureDialog'
import { MembershipIconCircled } from 'src/components/Membership/MembershipIcon'
import { openMembershipMoreCreditsModal } from 'src/components/Membership/MembershipMoreCreditsModal'
import { UIButton } from 'src/components/ui/Button'
import { UISelect, UISelectItem } from 'src/components/ui/Select'
import { UISpinnerLoading } from 'src/components/ui/SpinnerLoading'
import Text from 'src/components/ui/Text'
import type {
  GenerationModel,
  GenerationModelVersion,
} from 'src/services/apollo/generated/graphql'
import { GenerationModelType } from 'src/services/apollo/generated/graphql'
import {
  useGetGenerationModelByVersionId,
  usePagedListMyBookmarkedGenerationModels,
} from 'src/services/apollo/generation-model'
import { usePresetModels } from 'src/services/helpers/model'
import type { GenerationModelVersionExtra } from 'src/services/helpers/schema/modelVersionExtra'
import { useMe } from 'src/services/helpers/user'
import { cx } from 'src/utils/cx'
import {
  getDefaultParamsFromModelVersionExtra,
  getParamsForModelChange,
} from 'src/utils/generationModel/modelParams'
import { getNodesFromConnection } from 'src/utils/graphql'
import { getThumbnailUrl } from 'src/utils/media'
import {
  hasModelEnabledLightning,
  isXLModel,
  LIGHTNING_MODE_DEFAULT_PARAMS,
} from 'src/utils/model'
import { isHiResEnabled } from 'src/utils/task'
import { track } from 'src/utils/tracker'
import { useTaskSubmitFormContext } from '../../BaseTaskSubmitForm'
import { EmptyModelListInSidebar } from './ModelDialogComp'
import {
  ModelFormContext,
  ModelFormContextProvider,
} from './ModelSelectContext'
import { type VersionConfirmHandler } from './ModelSelectPreview'

const MODEL_SELECT_MODE_STORAGE_KEY = 'generator::model-select-list-mode'

/**
 * Member-only feature
 * Indicates which mode is currently enabled in the sidebar
 * @returns
 */
export function useModelSelectModeStorage() {
  return useLocalStorage<'preset' | 'bookmark'>({
    key: MODEL_SELECT_MODE_STORAGE_KEY,
    defaultValue: 'preset',
  })
}

export default function ModelSelect(props: {
  /**
   * user model Id
   */
  modelId?: string
}) {
  const [mode] = useModelSelectModeStorage()

  return (
    <ModelFormContextProvider>
      <CurrentModel {...props} />
      <ModelQuickSelectInSidebar mode={mode} />
    </ModelFormContextProvider>
  )
}

export function ModelModeToggle(
  props: {
    mode: 'preset' | 'bookmark'
    setMode: Dispatch<SetStateAction<'preset' | 'bookmark'>>
  } & StyleProps,
) {
  const { t } = useTranslation(['generation'])
  const { mode, setMode, className } = props
  const { beingMember, me } = useMe()
  const icon = !beingMember ? (
    <MembershipIconCircled className="size-4" />
  ) : (
    <MingcuteTransferLine className="text-feat-brand-100" />
  )

  const onToggle = useCallback(() => {
    if (!me?.id)
      return openSignModal(null, {
        fromPath: 'genpage-sidebar-bookmark',
      })
    if (!beingMember) {
      openMembershipMoreCreditsModal({
        defaultMode: 'plans',
        trackingLocation: 'genpage-sidebar-bookmark',
      })
    } else {
      setMode(prev => (prev === 'preset' ? 'bookmark' : 'preset'))
    }
  }, [beingMember, !!me?.id])

  useEffect(() => {
    // Reset mode to preset if user is no longer a member
    if (!beingMember && mode === 'bookmark') {
      setMode('preset')
    }
  }, [beingMember, mode])

  return (
    <UIButton
      variant="ghost"
      icon={icon}
      onPress={onToggle}
      className={cx(
        className,
        'px-1 py-[2px] font-semibold text-xs gap-1 focus:outline-1 focus:outline-purple-400',
      )}
    >
      <span
        className={cx(mode === 'preset' && beingMember && 'text-theme-primary')}
      >
        {mode === 'preset'
          ? t('generation:model.select.mode.bookmark')
          : t('generation:model.select.mode.preset')}
      </span>
    </UIButton>
  )
}

function CurrentModel({ modelId: modelIdValue }: { modelId?: string }) {
  const { data: modelVersion, loading: versionLoading } =
    useGetGenerationModelByVersionId(modelIdValue || skipToken)
  const modelSupportsLightning = hasModelEnabledLightning(modelVersion?.extra)

  return (
    <>
      {modelIdValue && (
        <CurrentModelContent
          modelVersionId={modelIdValue}
          modelVersion={modelVersion}
          loading={versionLoading}
          modelSupportsLightning={modelSupportsLightning}
        />
      )}
    </>
  )
}

type ModelMode = 'lightning' | 'quality'

function ModelModeSelect(props: {
  modelVersion?: GenerationModelVersion
  modelSupportsLightning?: boolean
}) {
  const { modelVersion, modelSupportsLightning } = props

  function getQualityModelDefaultParams() {
    const modelDefaultParams = getDefaultParamsFromModelVersionExtra(
      {
        versionExtra: modelVersion?.extra,
        modelId: modelVersion?.model?.id,
        modelType: modelVersion?.model?.type,
      },
      {
        enableLightningMode: false,
      },
    )
    return {
      // reset cfgScale and samplingSteps to default
      ...pick(modelDefaultParams, ['cfgScale', 'samplingSteps']),
      lightning: false,
    }
  }

  const { t } = useTranslation(['generation'])

  const { values, setValues } = useTaskSubmitFormContext()
  const { beingMember } = useMe()

  const modelMode = values.lightning ? 'lightning' : 'quality'

  const onModelModeChange = useCallback(
    (mode: ModelMode) => {
      const defaultParams = match(mode)
        .with('quality', () => getQualityModelDefaultParams())
        .with('lightning', () => LIGHTNING_MODE_DEFAULT_PARAMS)
        .exhaustive()
      setValues(prev => ({
        ...prev,
        ...defaultParams,
      }))
    },
    [modelVersion, getQualityModelDefaultParams],
  )

  if (!modelSupportsLightning) {
    return null
  }

  return (
    <div className="flex items-center gap-2">
      <UISelect
        className="min-w-16"
        items={[
          {
            key: 'lightning',
            name: t('generation:model.lightning.mode.lightning'),
            startIcon: <EmojioneV1LightningMood className="size-4" />,
          },
          {
            key: 'quality',
            name: t('generation:model.lightning.mode.quality'),
            startIcon: <MembershipIconCircled className="size-4" />,
          },
        ]}
        selectedKey={modelMode}
        onSelectionChange={v => {
          if (v === 'quality' && !beingMember) {
            openMembershipFeatureDialog({
              dialogBody: (
                <Text i18nKey="membership:membership-only-feature-dialog.content.lightning-mode-quality" />
              ),
              trackingLocation: 'genpage-sidebar-model',
            })
            return
          }

          onModelModeChange(v as ModelMode)
        }}
      >
        {item => (
          <UISelectItem id={item.key} startIcon={item.startIcon}>
            {item.name}
          </UISelectItem>
        )}
      </UISelect>

      <UITooltipHelpIcon TooltipProps={{ placement: 'start' }}>
        <p className="p-4 text-sm min-w-[270px] whitespace-pre-wrap">
          <Text
            i18nKey="generation:model.lightning.help.description"
            components={{ br: <br />, b: <b /> }}
          />
        </p>
      </UITooltipHelpIcon>
    </div>
  )
}

/**
 * Judge whether the current model area needs to show capability tags.
 * By 2025.7's design, if the `modelVersion?.extra?.capabilities` exists,
 * capability tags should show, otherwise not.
 */
function hasModelEnabledCapabilityTags(props: {
  modelVersionExtra?: GenerationModelVersionExtra
}) {
  return Boolean(props.modelVersionExtra?.capabilities)
}

function ModelCapabilityTags(props: {
  modelVersionExtra: GenerationModelVersionExtra
}) {
  const { modelVersionExtra } = props
  const capabilities: string[] = modelVersionExtra.capabilities

  function CapabilityTag(props: { capability: string }) {
    return (
      <div className="inline bg-purple-300 rounded-xl px-2 mt-1 text-xs leading-4 truncate dark:bg-purple-600 dark:text-neutral-300">
        <Text i18nKey={`market:model.capability.${props.capability}`} />
      </div>
    )
  }

  return (
    <>
      {capabilities.map(capability => (
        <CapabilityTag key={capability} capability={capability} />
      ))}
    </>
  )
}

export function CurrentModelContent(props: {
  modelVersionId?: string
  modelVersion?: GenerationModelVersion
  loading: boolean
  modelSupportsLightning?: boolean
}) {
  const { modelVersion, loading, modelVersionId, modelSupportsLightning } =
    props
  const { t } = useTranslation(['generation'])
  const modelImage = getThumbnailUrl(modelVersion?.media)

  const imageVariant = tv({
    base: 'shrink-0 border border-px rounded-xl mb-1',
    variants: {
      size: {
        withTag: 'w-12 h-12',
        default: 'w-16 h-16',
      },
    },
  })

  return (
    <div className="px-4 py-2 bg-background-light rounded-xl">
      <div className="flex flex-row items-center justify-start gap-4">
        <div
          className={imageVariant({
            size: hasModelEnabledCapabilityTags({
              modelVersionExtra: modelVersion?.extra,
            })
              ? 'withTag'
              : 'default',
          })}
        >
          {modelImage && (
            <img
              src={modelImage}
              alt=""
              loading="eager"
              decoding="sync"
              className="rounded-xl w-full h-full object-cover"
            />
          )}
        </div>

        <p className="font-semibold text-sm break-all hyphens-auto max-w-[150px] relative">
          {!modelVersion ? (
            <UISpinnerLoading className="size-10" loading={loading}>
              <span className="w-full italic">
                {t('generation:model.unavailable', {
                  modelName: modelVersionId,
                })}
              </span>
            </UISpinnerLoading>
          ) : (
            <>
              <Link to={`/model/${modelVersion.modelId}`} target="_blank">
                {modelVersion.model?.title}
              </Link>
              <Link
                to={`/model/${modelVersion.modelId}/${modelVersion.id}`}
                target="_blank"
                className="block font-semibold font-mono text-xs text-primary-light truncate"
              >
                {modelVersion.name}
              </Link>
              <ModelModeSelect
                modelVersion={modelVersion}
                modelSupportsLightning={modelSupportsLightning}
              />
            </>
          )}
        </p>
      </div>
      {hasModelEnabledCapabilityTags({
        modelVersionExtra: modelVersion?.extra,
      }) ? (
        <div className="flex gap-1 flex-wrap border-dashed border-t-neutral-300">
          <ModelCapabilityTags modelVersionExtra={modelVersion!.extra} />
        </div>
      ) : null}
    </div>
  )
}

function ModelQuickSelectInSidebar(props: { mode: 'preset' | 'bookmark' }) {
  const [modelFormState, setModelFormState] = useContext(ModelFormContext)

  const { values, setValues } = useTaskSubmitFormContext()
  const { modelId: modelIdValue } = values

  const [openDialog, setOpenDialog] = useState(false)

  const { t } = useTranslation(['artwork', 'action', 'generation'])

  const { data: userBookmarkListData, loading: userBookmarkListLoading } =
    usePagedListMyBookmarkedGenerationModels(
      props.mode === 'bookmark'
        ? {
            modelType: GenerationModelType.AnyModel,
            first: 6,
          }
        : skipToken,
    )

  const { data: selectedVersionData } = useGetGenerationModelByVersionId(
    modelIdValue ?? skipToken,
  )

  const beingXL = isXLModel(selectedVersionData?.modelType)
  const { modelList: presetModelList, loading: presetModelListLoading } =
    usePresetModels()

  const bookmarkModelList = getNodesFromConnection(userBookmarkListData)

  const modelList =
    props.mode === 'preset' ? presetModelList : bookmarkModelList
  const listLoading =
    props.mode === 'preset' ? presetModelListLoading : userBookmarkListLoading

  // Sync version data to context if not already
  useEffect(() => {
    if (!selectedVersionData) return
    if (modelIdValue && modelIdValue !== modelFormState.versionId) {
      setModelFormState(prev => ({
        ...prev,
        searchKeyword: '',
        searchKeywordForInput: '',
        versionId: modelIdValue,
        type: 'preset',
        modelId: selectedVersionData.modelId,
        openDetail: false,
      }))
    }
  }, [modelIdValue, modelFormState.versionId, selectedVersionData])

  const hiresEnabled = isHiResEnabled(values.upscale)
  const onModelChange = useCallback(
    (model: GenerationModel) => {
      const { id, title, type, latestAvailableVersion, extra } = model
      const versionId = latestAvailableVersion?.id

      // Cannot change model without a version
      // This should not happen
      if (!versionId) return

      track('outer_model_click', {
        modelId: id,
        location: props.mode,
      })
      setModelFormState(prev => ({
        ...prev,
        type: 'preset',
        versionId: latestAvailableVersion?.id || null,
        modelId: id,
        name: title,
        openDetail: false,
      }))
      const versionExtra = latestAvailableVersion?.extra
      const modelExtra = extra

      const { values: valuesToApply, hasResetHires } = getParamsForModelChange(
        {
          versionId,
          modelId: id,
          versionExtra,
          modelType: type,
          modelExtra: modelExtra,
          currentModelType: selectedVersionData?.modelType,
        },
        {
          hiresEnabled,
          enableLightningMode: true,
          currentWidth: values.width,
          currentHeight: values.height,
        },
      )
      setValues(prev => ({
        ...prev,
        ...valuesToApply,
      }))
      hasResetHires &&
        toast.info(t('generation:hires.notice.settings-reset-by-model-change'))
    },
    [
      props.mode,
      hiresEnabled,
      selectedVersionData?.modelType,
      values.width,
      values.height,
    ],
  )

  const onVersionChangeForDialog = useCallback<VersionConfirmHandler>(
    arg => {
      const { version, model, id } = arg
      const { values: valuesToApply, hasResetHires } = getParamsForModelChange(
        {
          modelId: version?.modelId,
          versionId: id,
          versionExtra: version?.extra,
          modelType: version?.modelType,
          modelExtra: model?.extra,
          currentModelType: selectedVersionData?.modelType,
        },
        {
          hiresEnabled,
          enableLightningMode: true,
          currentWidth: values.width,
          currentHeight: values.height,
        },
      )
      setValues(prev => ({
        ...prev,
        ...valuesToApply,
      }))
      if (hasResetHires) {
        toast.info(t('generation:hires.notice.settings-reset-by-model-change'))
      }
    },
    [
      beingXL,
      hiresEnabled,
      selectedVersionData?.modelType,
      values.width,
      values.height,
    ],
  )

  return (
    <>
      <FormControl className="gap-2">
        {modelList.length < 1 ? (
          listLoading ? (
            <div className="w-full flex justify-center items-center h-20">
              <UISpinnerLoading
                className="size-10"
                loading={listLoading}
              ></UISpinnerLoading>
            </div>
          ) : (
            <EmptyModelListInSidebar
              className="h-[var(--list-height)] py-4 px-1 text-center text-neutral-400 dark:text-neutral-400 text-sm"
              mode={props.mode}
            />
          )
        ) : (
          <RadioGroup
            value={modelIdValue ? modelFormState.modelId : 'uploaded'}
            className="grid grid-cols-3 gap-3 mt-2 w-full items-start justify-items-stretch"
          >
            {modelList.slice(0, 6).map(model => {
              return (
                <ModelControlLabel
                  key={model.id}
                  control={
                    <Radio
                      className="sr-only"
                      onChange={() => {
                        onModelChange(model)
                      }}
                    />
                  }
                  selected={modelIdValue === model.latestAvailableVersion?.id}
                  modelId={model.id}
                  modelType={model.type}
                  modelCurations={model.curations}
                  title={model.title}
                  media={model.media}
                  badge={<ModelCornerBadge model={model} />}
                />
              )
            })}
            {/* needs a placeholding radio to properly unset radio group
                when task with bookmarked model is finished, model value will become "uploaded"*/}
            <Radio className="hidden" value="uploaded" />
          </RadioGroup>
        )}
      </FormControl>

      <UIButton
        color="grayish-sidebar"
        variant="filled"
        className="capitalize"
        onPress={() => {
          track('show_more_model_click', { location: props.mode })
          setOpenDialog(true)
        }}
      >
        {t('generation:model.select.show-more-model')}
      </UIButton>

      <ModelSelectDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        initState={modelFormState}
        onVersionSelected={version => {
          onVersionChangeForDialog(version)
        }}
      />
    </>
  )
}
